import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import ContentEditorField from "components/ContentEditor/ContentEditorField";
import { DangerButton } from "components/atoms/DangerButton";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { ErrorWrapper } from "components/organisms/form/BaseField";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import TextField from "components/organisms/form/TextField";
import ToggleField from "components/organisms/form/ToggleField";
import {
	useAllInfoTextsQuery,
	useCreateInfoTextMutation,
	useDeleteInfoTextMutation,
	useSingleInfoTextQuery,
	useUpdateInfoTextMutation,
} from "pages/Content/InfoTextPages/InfoTextPages.graphql-gen";

import { InfoTextInput } from "generated/graphql-types";

function getVariablesFromText(currentText: string | undefined): string[] {
	if (!currentText) return [];
	const regex = /\[([a-zA-Z_][a-zA-Z0-9_]*)\]/g;
	const matches = currentText.match(regex) || [];
	return matches.map((match) => match.slice(1, -1));
}

const InfoTextSchema = z.object({
	active: z.boolean(),
	image: z
		.object({
			url: z.string().optional(),
			reference: z.string().optional(),
			provider: z.string().optional(),
		})
		.optional(),
	title: z.string().optional(),
	content: z
		.object({
			html: z.string().optional(),
			json: z.string().optional(),
		})
		.optional(),
	comment: z.string().optional(),
	key: z.string(),
});

export default function EditInfoTextPage() {
	const { data: allInfoTexts } = useAllInfoTextsQuery();
	const { infoTextId } = useParams<{ infoTextId?: string }>();
	const navigate = useNavigate();
	const [updateInfoText, { loading, error }] = useUpdateInfoTextMutation();
	const [createInfoText] = useCreateInfoTextMutation();
	const [deleteInfoText] = useDeleteInfoTextMutation();
	const [variables, setVariables] = useState<string[]>([]);
	const [keyIsInUse, setKeyIsInUse] = useState(false);

	async function upsertInfoText(infoText: InfoTextInput) {
		if (infoText.id) {
			const result = await updateInfoText({ variables: { infoText } });
			return result.data?.updateInfoText;
		} else {
			const result = await createInfoText({ variables: { infoText } });
			return result.data?.createInfoText;
		}
	}

	function keyIsAlreadyInUse(key: string) {
		return allInfoTexts?.allInfoTexts?.find((it) => it.key === key && it.id !== infoTextId) !== undefined;
	}

	const { data } = useSingleInfoTextQuery({
		variables: {
			infoTextId: infoTextId ?? "",
		},
		skip: !infoTextId,
	});

	useEffect(() => setVariables(getVariablesFromText(data?.singleInfoText?.content?.html)), [data?.singleInfoText?.content]);

	return (
		<div>
			<MainLayoutPageHeader title="Ny infotekst" />
			<Flex gap="16px">
				<Form
					key={data?.singleInfoText?.id}
					loading={loading}
					value={
						data?.singleInfoText ?? {
							content: {
								json: "",
								html: "",
							},
						}
					}
					onSubmit={async (newData) => {
						if (keyIsInUse) {
							return;
						}
						upsertInfoText({
							id: data?.singleInfoText?.id,
							...newData,
						}).then(() => navigate(`/info-text`));
					}}
					schema={InfoTextSchema}
				>
					<Flex direction={Direction.column} gap="16px" style={{ paddingTop: "16px" }}>
						<ToggleField name="active" label="Publisert?" />
						<TextField name="title" label="Tittel" required />
						<TextField name="comment" label="Kommentar" />
						<ContentEditorField name="content" label="Innhold" onChange={(e) => setVariables(getVariablesFromText(e.html))} required />
						<TextField required name="key" label="Nøkkel" onChange={(e) => setKeyIsInUse(keyIsAlreadyInUse(e.currentTarget.value))} />
						{keyIsInUse && <p style={{ color: "red" }}>Nøkkel er allerede i bruk!</p>}
						<TextField disabled label="Aktive variabler" value={variables.join(", ")} />
						{error && <ErrorWrapper>{error.message}</ErrorWrapper>}
						<FormButton loading={loading} label={`${data?.singleInfoText?.id ? "Oppdater" : "Opprett"} infotekst`} />
						{data?.singleInfoText?.id && (
							<DangerButton
								onClick={async () => {
									if (!window.confirm("Sikker på at du ønsker å slette " + data.singleInfoText?.title)) {
										return;
									}
									await deleteInfoText({
										variables: {
											infoTextId: data?.singleInfoText?.id!,
										},
									});

									navigate(`/info-text`);
								}}
								label="Slett infotekst"
							/>
						)}
					</Flex>
				</Form>
			</Flex>
		</div>
	);
}
